<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfig } from 'dashboard/composables/useConfig';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ContactDetailsItem from './ContactDetailsItem.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
  inboxId: { type: Number, default: undefined },
});

const { t } = useI18n();
const { hostURL } = useConfig();
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

const selectedInboxId = ref(props.inboxId);
const selectedLabelTitle = ref('');

const webWidgetInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'Channel::WebWidget' && inbox.website_token
  )
);

const accountLabels = computed(() => labels.value || []);

const currentInbox = computed(() => {
  const id = selectedInboxId.value || props.inboxId;
  return inboxes.value.find(inbox => inbox.id === id);
});

const generatedUrl = computed(() => {
  const inbox = currentInbox.value;
  if (!inbox?.website_token || !selectedLabelTitle.value) return '';

  const base = `${hostURL || ''}/widget`;
  const params = new URLSearchParams({
    website_token: inbox.website_token,
    direct_label: selectedLabelTitle.value,
    skip_triage: '1',
  });
  return `${base}?${params.toString()}`;
});

const canGenerate = computed(
  () => webWidgetInboxes.value.length > 0 && accountLabels.value.length > 0
);

const copyLink = async () => {
  if (!generatedUrl.value) return;
  try {
    await navigator.clipboard.writeText(generatedUrl.value);
    useAlert(t('CONVERSATION_SIDEBAR.DIRECT_LINK.COPIED'));
  } catch {
    useAlert(t('CONVERSATION_SIDEBAR.DIRECT_LINK.COPY_ERROR'));
  }
};

onMounted(() => {
  if (
    props.inboxId &&
    webWidgetInboxes.value.some(i => i.id === props.inboxId)
  ) {
    selectedInboxId.value = props.inboxId;
  }
  if (accountLabels.value.length > 0 && !selectedLabelTitle.value) {
    selectedLabelTitle.value = accountLabels.value[0].title;
  }
});
</script>

<template>
  <div class="space-y-2">
    <p
      v-if="!canGenerate"
      class="text-xs text-n-slate-11 px-2"
    >
      {{ $t('CONVERSATION_SIDEBAR.DIRECT_LINK.REQUIRES_LABELS') }}
    </p>
    <div
      v-else
      class="space-y-2"
    >
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.DIRECT_LINK.TITLE')"
    />
    <p class="text-xs text-n-slate-11 px-2">
      {{ $t('CONVERSATION_SIDEBAR.DIRECT_LINK.DESCRIPTION') }}
    </p>
    <div class="multiselect-wrap--small">
      <label class="text-xs font-medium text-n-slate-11 px-2">
        {{ $t('CONVERSATION_SIDEBAR.DIRECT_LINK.INBOX') }}
      </label>
      <select
        v-model="selectedInboxId"
        class="w-full rounded border border-n-slate-6 bg-n-surface-2 px-2 py-1.5 text-sm"
      >
        <option
          v-for="inbox in webWidgetInboxes"
          :key="inbox.id"
          :value="inbox.id"
        >
          {{ inbox.name }}
        </option>
      </select>
    </div>
    <div class="multiselect-wrap--small">
      <label class="text-xs font-medium text-n-slate-11 px-2">
        {{ $t('CONVERSATION_SIDEBAR.DIRECT_LINK.CATEGORY') }}
      </label>
      <select
        v-model="selectedLabelTitle"
        class="w-full rounded border border-n-slate-6 bg-n-surface-2 px-2 py-1.5 text-sm"
      >
        <option
          v-for="label in accountLabels"
          :key="label.id"
          :value="label.title"
        >
          {{ label.title }}
        </option>
      </select>
    </div>
    <div class="px-2">
      <NextButton
        :disabled="!generatedUrl"
        block
        size="small"
        icon="i-lucide-copy"
        :label="$t('CONVERSATION_SIDEBAR.DIRECT_LINK.COPY_LINK')"
        @click="copyLink"
      />
    </div>
    </div>
  </div>
</template>
